import React, { useState } from 'react';
import { BarChart3Icon, TrendingUpIcon, TrendingDownIcon } from 'lucide-react';
type Notification = {
  id: string;
  event: string;
  date: string;
  impact: 'high_negative' | 'medium_negative' | 'low_positive' | 'medium_positive' | 'high_positive';
  acknowledgedBy: string | null;
};
const notifications: Notification[] = [{
  id: '1',
  event: '67% increase in DBT in the past month (from 6 days to 10 days)',
  date: '3/16/2026',
  impact: 'high_negative',
  acknowledgedBy: null
}, {
  id: '2',
  event: 'C-Suite Change: CFO David Wallace departed the company',
  date: '3/02/2026',
  impact: 'high_negative',
  acknowledgedBy: null
}, {
  id: '3',
  event: 'Credit limit increased from $35,000 to $50,000 based on updated financials',
  date: '2/20/2026',
  impact: 'high_positive',
  acknowledgedBy: 'Dave Davidson'
}, {
  id: '4',
  event: 'Negative news article: Regional Manager Hosts Unsanctioned "Office Olympics" — HR Investigation Launched',
  date: '2/28/2026',
  impact: 'medium_negative',
  acknowledgedBy: null
}, {
  id: '5',
  event: 'Pulse Score increased from 74 to 82 (+8 points)',
  date: '1/15/2026',
  impact: 'high_positive',
  acknowledgedBy: 'Dave Davidson'
}];
function ImpactIndicator({
  impact
}: {
  impact: Notification['impact'];
}) {
  if (impact === 'high_negative') {
    return <div className="flex items-end gap-0.5 justify-center" data-id="element-1493">
        <div className="w-1.5 h-2 bg-red-500 rounded-sm" data-id="element-1494" />
        <div className="w-1.5 h-3.5 bg-red-500 rounded-sm" data-id="element-1495" />
        <div className="w-1.5 h-5 bg-red-500 rounded-sm" data-id="element-1496" />
      </div>;
  }
  if (impact === 'medium_negative') {
    return <div className="flex items-end gap-0.5 justify-center" data-id="element-1497">
        <div className="w-1.5 h-2 bg-red-500 rounded-sm" data-id="element-1498" />
        <div className="w-1.5 h-3.5 bg-red-500 rounded-sm" data-id="element-1499" />
        <div className="w-1.5 h-5 bg-gray-200 rounded-sm" data-id="element-1500" />
      </div>;
  }
  if (impact === 'low_positive') {
    return <div className="flex items-end gap-0.5 justify-center" data-id="element-1501">
        <div className="w-1.5 h-2 bg-green-500 rounded-sm" data-id="element-1502" />
        <div className="w-1.5 h-3.5 bg-gray-200 rounded-sm" data-id="element-1503" />
        <div className="w-1.5 h-5 bg-gray-200 rounded-sm" data-id="element-1504" />
      </div>;
  }
  if (impact === 'medium_positive') {
    return <div className="flex items-end gap-0.5 justify-center" data-id="element-1505">
        <div className="w-1.5 h-2 bg-green-500 rounded-sm" data-id="element-1506" />
        <div className="w-1.5 h-3.5 bg-green-500 rounded-sm" data-id="element-1507" />
        <div className="w-1.5 h-5 bg-gray-200 rounded-sm" data-id="element-1508" />
      </div>;
  }
  // high_positive
  return <div className="flex items-end gap-0.5 justify-center" data-id="element-1509">
      <div className="w-1.5 h-2 bg-green-500 rounded-sm" data-id="element-1510" />
      <div className="w-1.5 h-3.5 bg-green-500 rounded-sm" data-id="element-1511" />
      <div className="w-1.5 h-5 bg-green-500 rounded-sm" data-id="element-1512" />
    </div>;
}
export function NotificationsTab() {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const toggleAll = () => {
    if (selected.size === notifications.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(notifications.map(n => n.id)));
    }
  };
  const toggleOne = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    setSelected(next);
  };
  return <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden" data-id="element-1513">
      <div className="p-5 border-b border-gray-200" data-id="element-1514">
        <h3 className="text-lg font-bold text-gray-900" data-id="element-1515">Notifications</h3>
      </div>
      <div className="overflow-x-auto" data-id="element-1516">
        <table className="w-full text-sm text-left" data-id="element-1517">
          <thead className="bg-gray-50 text-gray-500 border-b border-gray-200" data-id="element-1518">
            <tr data-id="element-1519">
              <th className="px-5 py-3 w-10" data-id="element-1520">
                <input type="checkbox" checked={selected.size === notifications.length} onChange={toggleAll} className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer" data-id="element-1521" />
              </th>
              <th className="px-5 py-3 font-medium" data-id="element-1522">Event</th>
              <th className="px-5 py-3 font-medium text-center w-32" data-id="element-1523">Date</th>
              <th className="px-5 py-3 font-medium text-center w-24" data-id="element-1524">Impact</th>
              <th className="px-5 py-3 font-medium text-center w-40" data-id="element-1525">
                Acknowledged By
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200" data-id="element-1526">
            {notifications.map(n => <tr key={n.id} className="hover:bg-gray-50 transition-colors" data-id="element-1527">
                <td className="px-5 py-4" data-id="element-1528">
                  <input type="checkbox" checked={selected.has(n.id)} onChange={() => toggleOne(n.id)} className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer" data-id="element-1529" />
                </td>
                <td className="px-5 py-4 text-gray-900" data-id="element-1530">{n.event}</td>
                <td className="px-5 py-4 text-gray-500 text-center whitespace-nowrap" data-id="element-1531">
                  {n.date}
                </td>
                <td className="px-5 py-4 text-center" data-id="element-1532">
                  <ImpactIndicator impact={n.impact} data-id="element-1533" />
                </td>
                <td className="px-5 py-4 text-gray-500 text-center" data-id="element-1534">
                  {n.acknowledgedBy ?? '--'}
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-end gap-4 px-5 py-3 border-t border-gray-200 text-sm text-gray-500" data-id="element-1535">
        <span data-id="element-1536">Rows per page: 5</span>
        <span data-id="element-1537">1–5 of 5</span>
        <div className="flex gap-1" data-id="element-1538">
          <button className="p-1 rounded hover:bg-gray-100 text-gray-400 cursor-not-allowed" data-id="element-1539">
            ‹
          </button>
          <button className="p-1 rounded hover:bg-gray-100 text-gray-400 cursor-not-allowed" data-id="element-1540">
            ›
          </button>
        </div>
      </div>
    </div>;
}